import {Component, EventEmitter, Input, OnDestroy, Output, ChangeDetectionStrategy} from '@angular/core';
import {Container, Image, KiButtonMapping, Tab, TextArea} from "../../../../model/layout.model";
import {LayoutElementUtil} from "../../../../service/layout/layout-element.service";
import {ScreenSizeObserverService} from "../../../../service/layout/screen-size-observer.service";
import {FileUploadComponent} from '../../../layout/element/file-upload/file-upload.component';
import {DatetimePickerComponent} from '../../../layout/element/datetime-picker/datetime-picker.component';
import {TextFieldComponent} from '../../../layout/element/text-field/text-field.component';
import {TerminalListComponent} from '../../../layout/element/terminal-list/terminal-list.component';
import {ComboBoxComponent} from '../../../layout/element/combo-box/combo-box.component';
import {TextAreaComponent} from '../../../layout/element/text-area/text-area.component';
import {TerminalLabelComponent} from '../../../layout/element/terminal-label/terminal-label.component';
import {DividerComponent} from '../../../common/divider/divider.component';

import {TextAreaContainerComponent} from "../../../layout/element/text-area-container/text-area-container.component";
import {
  ArdPopnachtTextAreaComponent
} from "../../../layout/element/ard-popnacht-text-area/ard-popnacht-text-area.component";
import {
  TextAreaCopyContainerComponent
} from "../../../layout/element/text-area-copy-container/text-area-copy-container.component";
import {KiButtonComponent} from "../../../layout/element/ki-button/ki-button.component";
import {ImageComponent} from "../../../layout/element/image/image.component";
import {MatIconModule} from "@angular/material/icon";
import {KartenZoomComponent} from "../../../common/karten-zoom/karten-zoom.component";
import {KiErgebnisBoxComponent} from "../../../common/ki-ergebnis-box/ki-ergebnis-box.component";
import {LadeQuellenBoxComponent} from "../../../common/lade-quellen-box/lade-quellen-box.component";
import {EventBusService} from "../../../../service/event-bus.service";
import {EventBusTypes} from "../../../../model/constants";
import {GrafikUmfang} from "../../../../model/event-bus.model";
import {HoechstwerteUebernahmeService} from "../../../../service/hoechstwerte-uebernahme.service";
import {
  GRAFIK_TOOLBAR,
  GraphicToolbarComponent,
  KI_TEXTE_TOOLBAR,
  COSMO_GRAFIK_TOOLBAR,
  COSMO_KI_TEXTE_TOOLBAR,
  LOKALZEIT_GRAFIK_TOOLBAR,
  LOKALZEIT_SAMSTAG_GRAFIK_TOOLBAR,
  LOKALZEIT_PROGNOSE_REITER,
  LOKALZEIT_PROGNOSE_TOOLBAR,
  LOKALZEIT_KI_TEXTE_TOOLBAR,
  ToolbarAction
} from "../graphic-toolbar/graphic-toolbar.component";

/** Aufeinanderfolgende Karten einer Zeile mit TV-Relevanz – ein lila Rahmen. */
export interface TvGruppe {
  karten: Image[];
}

@Component({
    selector: 'app-document-detail-elements',
    templateUrl: './document-detail-elements.component.html',
    styleUrls: ['./document-detail-elements.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DividerComponent, TerminalLabelComponent, TextAreaComponent, ComboBoxComponent, TerminalListComponent, TextFieldComponent, DatetimePickerComponent, FileUploadComponent, TextAreaContainerComponent, TextAreaCopyContainerComponent, ArdPopnachtTextAreaComponent, KiButtonComponent, ImageComponent, MatIconModule, GraphicToolbarComponent, KartenZoomComponent, KiErgebnisBoxComponent, LadeQuellenBoxComponent]
})
export class DocumentDetailElementsComponent implements OnDestroy {

  @Input() tab!: Tab;
  @Input() documentId?: string;
  /**
   * Beschriftung des Ober-Reiters ("Nordrhein-Westfalen", "COSMO", ...).
   *
   * Nötig, weil die Unter-Reiter beider Bereiche gleich heißen ("Heute", "KI-Texte"):
   * ohne den Bereich stünde über den COSMO-Karten die Überschrift von NRW.
   */
  @Input() bereich?: string;
  //@Input() readonly:boolean = false;
  @Output() copyEvent = new EventEmitter<String[]>();

  /** Gemerkte Umfänge je Zeile bzw. je Reiter – siehe {@link umfangVon}. */
  private readonly umfangCache = new WeakMap<Container, GrafikUmfang>();
  /** Gemerkte TV-Relevanz-Gruppen je Zeile – siehe {@link tvGruppeAb}. */
  private readonly tvGruppenCache = new WeakMap<Container, Map<Container, TvGruppe>>();

  constructor(public layoutElementUtil: LayoutElementUtil,
              private screenSizeObserver: ScreenSizeObserverService,
              private eventBusService: EventBusService,
              private hoechstwerte: HoechstwerteUebernahmeService) {

  }

  /**
   * Zeigt der TV-Relevanz-Rahmen dieser Gruppe den Knopf "Höchstwerte übernehmen"? Nur bei
   * Karten, die eine Quelle in der "ganz"-Zeile haben: "HEUTE: Nachmittag" (NRW) und
   * "MORGEN: Nachmittag" (Lokalzeiten), siehe {@link HoechstwerteUebernahmeService.quelleFuer}.
   */
  zeigtHoechstwerte(gruppe: TvGruppe): boolean {
    return HoechstwerteUebernahmeService.moeglich(gruppe.karten);
  }

  /** Klick auf "Höchstwerte übernehmen": Temperaturen der "ganz"-Zeile dieses Reiters in die Karten der Gruppe. */
  onHoechstwerte(event: Event, gruppe: TvGruppe): void {
    event.stopPropagation();
    this.hoechstwerte.uebernehmen(this.tab, gruppe.karten);
  }

  /**
   * Klick auf "leeren" neben einer Zeilenüberschrift: leert **nur die Karten dieser Zeile**.
   *
   * Adressiert wird über die `containerId` der Karten, nicht über die Überschrift: die
   * Zeilennamen ("HEUTE: ganz" usw.) wiederholen sich zwischen den Ober-Reitern – COSMO
   * führt dieselben Zeilen wie Nordrhein-Westfalen –, und da Material die Inhalte
   * nicht sichtbarer Reiter mitrendert, würde ein Namensvergleich dort mitlöschen.
   * Die Karten bleiben stehen, nur die Werte werden entfernt.
   */
  onClearBoxes(section: Container): void {
    this.eventBusService.emit(EventBusTypes.CLEAR_BOXES_EVENT.valueOf(), this.umfangVon(section));
  }

  /**
   * Klick auf "KI-Auswahl aufheben" neben einer Zeilenüberschrift: hakt **alle gelben
   * Kästchen der Karten dieser Zeile** ab – und nur dort. Die Werte der Boxen bleiben,
   * anders als beim "leeren". Adressiert wie "leeren" über die `containerId`s der Zeile.
   */
  onKiAuswahlAufheben(section: Container): void {
    this.eventBusService.emit(EventBusTypes.KI_AUSWAHL_AUFHEBEN_EVENT.valueOf(), this.umfangVon(section));
  }

  /**
   * Der Umfang einer Karten-Zeile – die Reichweite ihrer eigenen Knöpfe.
   *
   * ⚠️ Das Ergebnis ist **gemerkt**, und das ist nicht Sparsamkeit: es hängt an zwei
   * Template-Bindungen (`[umfang]` der Knopfreihe und der grünen Box). Ein bei jedem
   * Aufruf neu gebautes Objekt hätte jedes Mal eine neue Identität, und die
   * Verifizierungs-Prüfung im Entwicklungsmodus würde das als Änderung nach der Prüfung
   * melden (**NG0100**) und das Rendern abbrechen. Der `WeakMap`-Eintrag verschwindet
   * mit dem Dokumentbaum, wenn ein anderer Datensatz geladen wird.
   */
  umfangVon(section: Container): GrafikUmfang {
    const gemerkt = this.umfangCache.get(section);
    if (gemerkt) {
      return gemerkt;
    }
    const umfang = new GrafikUmfang(this.kartenIdsVon(section), 'zeile', section.caption ?? '');
    this.umfangCache.set(section, umfang);
    return umfang;
  }

  /**
   * Der Umfang dieses **Reiters** – die Reichweite der Leiste oben.
   *
   * Sie meint die Zeilen ihres Reiters und **nicht** das ganze Dokument. Das ist
   * fachlich zwingend: jeder Reiter ist ein anderer Zeitpunkt ("Heute", "2.
   * Aussichtentag"), und eine KI-Anfrage über alle Reiter hinweg erzeugt Texte über den
   * falschen Zeitraum. Gemerkt wird auch hier, aus demselben Grund wie oben.
   */
  umfangDesReiters(): GrafikUmfang {
    const gemerkt = this.umfangCache.get(this.tab);
    if (gemerkt) {
      return gemerkt;
    }
    const ids = (this.tab?.containerList ?? [])
      .flatMap(section => this.kartenIdsVon(section));
    const umfang = new GrafikUmfang(ids, 'reiter', this.tab?.caption ?? '');
    this.umfangCache.set(this.tab, umfang);
    return umfang;
  }

  /**
   * Die TV-Relevanz-Gruppe, die mit dieser Karte **beginnt** – oder null, wenn die Karte keine
   * Gruppe eröffnet (nicht markiert oder nur Mitglied einer Gruppe, die früher beginnt).
   *
   * Aufeinanderfolgende Karten einer Zeile mit gesetztem `tvRelevanz` (auch leer; `null`
   * heißt nicht markiert) bilden **eine** Gruppe und bekommen gemeinsam den lila Rahmen mit der
   * Überschrift "TV-Relevanz" – so wie "feine" und "sehr feine Unterteilung" nebeneinander.
   * Das Template entscheidet, welche Karten das sind (`Image.tvRelevanz`, Oktober 2026).
   *
   * Gemerkt je Zeile wie {@link umfangVon}: die Gruppe hängt an Template-Bindungen, ein jedes
   * Mal neues Objekt löste NG0100 aus.
   */
  tvGruppeAb(section: Container, container: Container): TvGruppe | null {
    return this.tvGruppenVon(section).get(container) ?? null;
  }

  /** Hat diese Zeile mindestens eine TV-Relevanz-Gruppe? Dann bekommt sie oben Platz für die Überschrift. */
  hatTvGruppe(section: Container): boolean {
    return this.tvGruppenVon(section).size > 0;
  }

  /** Trägt ein Rahmen dieser Zeile den Knopf "Höchstwerte übernehmen"? Dann noch mehr Platz oben. */
  hatTvKnopf(section: Container): boolean {
    return [...this.tvGruppenVon(section).values()].some(g => this.zeigtHoechstwerte(g));
  }

  /**
   * Breite des Rahmens: alle Karten der Gruppe plus die Abstände dazwischen plus der Überstand
   * links und rechts – als `calc()` in Prozent der **ersten** Karte, in der der Rahmen liegt.
   * Setzt voraus, dass die Karten einer Gruppe gleich breit sind (im Template sind es die
   * NRW-Karten mit derselben `flex`-Basis bzw. eine einzelne Karte).
   */
  tvBreite(gruppe: TvGruppe): string {
    const n = gruppe.karten.length;
    return `calc(${n} * 100% + ${n - 1} * var(--larger-margin) + 2 * ${DocumentDetailElementsComponent.TV_RAHMEN_UEBERSTAND})`;
  }

  /** Überstand des TV-Rahmens über die Kartenkante, links/rechts/unten (gleicher Wert im SCSS). */
  private static readonly TV_RAHMEN_UEBERSTAND = '6px';

  /** Hover-Text des Rahmens: die Sendungen, falls das Template welche nennt. */
  tvTitel(gruppe: TvGruppe): string {
    const sendungen = gruppe.karten.map(k => (k.tvRelevanz ?? '').trim()).find(s => s.length > 0);
    return sendungen ? `TV-Relevanz: ${sendungen}` : 'TV-Relevanz: diese Karten braucht die Redaktion für die TV-Sendungen';
  }

  private tvGruppenVon(section: Container): Map<Container, TvGruppe> {
    const gemerkt = this.tvGruppenCache.get(section);
    if (gemerkt) {
      return gemerkt;
    }
    const gruppen = new Map<Container, TvGruppe>();
    let offen: TvGruppe | null = null;
    for (const kind of section.containerList ?? []) {
      const karte = this.layoutElementUtil.isImage(kind) ? this.layoutElementUtil.asImage(kind) : null;
      if (karte && karte.tvRelevanz != null) {
        if (offen) {
          offen.karten.push(karte);
        } else {
          offen = {karten: [karte]};
          gruppen.set(kind, offen);
        }
      } else {
        offen = null;
      }
    }
    this.tvGruppenCache.set(section, gruppen);
    return gruppen;
  }

  /** Die `containerId`s der Karten einer Zeile, in Dokumentreihenfolge. */
  private kartenIdsVon(section: Container): string[] {
    return (section.containerList ?? [])
      .filter(child => this.layoutElementUtil.isImage(child))
      .map(child => child.containerId)
      .filter((id): id is string => !!id);
  }

  public isMobileScreen() {
    return this.screenSizeObserver.isSmallScreen() || this.screenSizeObserver.isXSmallScreen();
  }

  ngOnDestroy(): void {
    this.screenSizeObserver.destroy();
  }

  /**
   * Gehört dieser Reiter zu "Lokalzeit am Samstag"? Erkannt an der Karten-`containerId`
   * (`wdr.lokalzeit.am-samstag.…`), nicht an einer Beschriftung – die Zeitraum-Reiter
   * heißen in allen Städten gleich. Gemerkt, weil der Getter bei jeder Prüfung läuft.
   */
  private istLokalzeitAmSamstag(): boolean {
    if (this.samstagGemerkt?.tab !== this.tab) {
      this.samstagGemerkt = {tab: this.tab, wert: this.enthaeltKarteMit(this.tab?.containerList ?? [],
        'wdr.lokalzeit.am-samstag.')};
    }
    return this.samstagGemerkt.wert;
  }

  private samstagGemerkt?: {tab: unknown; wert: boolean};

  private enthaeltKarteMit(container: Container[], praefix: string): boolean {
    return container.some(kind => (kind.containerType === 'Image' && !!kind.containerId?.startsWith(praefix))
      || this.enthaeltKarteMit(kind.containerList ?? [], praefix));
  }

  /** Enthält dieser Reiter beschriftete Karten-Zeilen? */
  hasMapSections(): boolean {
    return !!this.tab?.containerList?.some(section => this.isMapSectionCaption(section));
  }

  /**
   * Kopfleiste dieses Reiters, oder null wenn keine gezeigt wird.
   * Liefert bewusst konstante Objekte zurück, damit die Bindings stabil bleiben.
   * Der Reiter wird derzeit noch an seiner Beschriftung erkannt – sobald die
   * Buttons Funktionen bekommen, gehört das ins Server-Template.
   */
  get toolbar(): { title: string; actions: ToolbarAction[] } | null {
    const cosmo = this.bereich === 'COSMO';
    const lokalzeit = this.bereich === 'Lokalzeiten';
    if (this.tab?.caption === 'KI-Texte') {
      if (cosmo) return COSMO_KI_TEXTE_TOOLBAR;
      return lokalzeit ? LOKALZEIT_KI_TEXTE_TOOLBAR : KI_TEXTE_TOOLBAR;
    }
    if (this.hasMapSections()) {
      if (cosmo) return COSMO_GRAFIK_TOOLBAR;
      if (lokalzeit) {
        if (this.tab?.caption === LOKALZEIT_PROGNOSE_REITER) {
          return LOKALZEIT_PROGNOSE_TOOLBAR;
        }
        return this.istLokalzeitAmSamstag() ? LOKALZEIT_SAMSTAG_GRAFIK_TOOLBAR : LOKALZEIT_GRAFIK_TOOLBAR;
      }
      return GRAFIK_TOOLBAR;
    }
    return null;
  }

  /**
   * Ist das die beschriftete Überschrift einer Karten-Zeile ("HEUTE: ganz", ...)?
   * Nur diese bekommt die größere, fette Darstellung mit mehr Abstand – alle
   * übrigen Reiter bleiben unverändert, ebenso die unbeschrifteten Grafik-Sections.
   */
  isMapSectionCaption(section: Container): boolean {
    return !!section.caption
      && !!section.containerList?.some(child => this.layoutElementUtil.isImage(child));
  }

  sectionHasDivider(sectionContainer: Container) {
    //console.log('sectionContainer: ', JSON.stringify(this.tab));
    const section = this.layoutElementUtil.asSection(sectionContainer);
    return section && section.showDivider;
  }

  getMouseEvent(ws: string[], section: Container, popnacht: Container) {
    /*console.log("GET_MOUSE_EVENT " + ws);
    console.debug("GET_MOUSE_EVENT SECTION: " + this.tab.caption);
    console.debug("GET_MOUSE_EVENT POPNACHT: " + popnacht.caption); */
    let contents: string = '';
    this.tab.containerList?.forEach(container => {
      if (container.containerType === 'Section') {
        container.containerList?.forEach(c => {
          if (ws.includes(c.containerId) && this.layoutElementUtil.isTextArea(c)) {
            const textArea: TextArea = this.layoutElementUtil.asTextArea(c);
            const popnachtContainer = this.layoutElementUtil.asPopnachtTextArea(popnacht);
            if (textArea.content) {
              if(popnachtContainer.content) {
                popnachtContainer.content = popnachtContainer.content + ' ' + textArea.content;
              } else {
                popnachtContainer.content = textArea.content;
              }
            }
          }
        })
      }
    })
  }

  getACEvent(ws: String[]) {
    if(ws) {
      this.copyEvent.emit(ws);
    }
  }
}
